// v2.9.2 — Control accionable de SIGUIENTE ACCIÓN (Central Día 1 · continuidad operativa).
// Cablea SÓLO los endpoints Día 1 que YA existen: /casos/{aid}/acciones y
// /casos/{aid}/verificacion-desenlace. El backend sigue siendo autoritativo: este control
// únicamente presenta lo que siguiente_accion/acciones_permitidas/resultados_permitidos indican.
// NO reintroduce controles legacy (iniciar verificación / marcar no verificable / escalar / checklist).
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { fmtTimestamp } from "@/lib/format";
import { PhoneCall, ClipboardList, MessageSquare } from "lucide-react";

const CONTACTO_RESULTADOS = ["confirmado_contacto", "no_responde", "comunicacion_incompleta"];
const CHIP = "rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors border-slate-200 text-slate-600 dark:text-slate-200 hover:border-[#01CBE3] hover:text-[#0891A6] disabled:opacity-50";

export default function SiguienteAccionControl({ detail, onRegistrarAccion, onRegistrarDesenlace, onResolverSeguimiento, onRegistrarEscalamiento, onNotaGestion, drafts, busy }) {
  const sa = detail?.siguiente_accion || {};
  const kind = sa.kind;
  const permitidas = detail?.acciones_permitidas || [];
  const resultados = detail?.resultados_permitidos || [];
  // ATN-01 · M6.6 — el borrador vive por CASO + ACTOR y, dentro de eso, por FORMULARIO: cambiar de
  // caso no mueve el texto al nuevo caso ni mezcla la nota de atención con la de escalamiento.
  const [notaLocal, setNotaLocal] = useState({});
  const casoId = detail?.id;
  const notaDe = (f) => (drafts ? drafts.get(casoId, f) : (notaLocal[f] ?? ""));
  const setNotaDe = (f, v) => { if (drafts) drafts.set(casoId, f, v); else setNotaLocal((p) => ({ ...p, [f]: v })); };
  const limpiaNota = (f) => { if (drafts) drafts.clear(casoId, f); else setNotaLocal((p) => ({ ...p, [f]: "" })); };
  // REPARACIÓN M6-F — una respuesta tardía sólo puede tocar el estado de SU caso de origen.
  const casoRef = useRef(casoId); casoRef.current = casoId;
  const resuelto = (r) => !r || r.estado === undefined || String(r.estado).startsWith("confirmado");
  // Limpieza ligada al origen: el borrador se borra por su propia clave de caso, y las selecciones
  // locales sólo se tocan si seguimos en ese mismo caso.
  const limpiaOrigen = (cid, forms = [], locales = () => {}) => {
    forms.forEach((f) => { if (drafts) drafts.clear(cid, f); else setNotaLocal((p) => ({ ...p, [f]: "" })); });
    if (casoRef.current === cid) locales();
  };
  const [segMotivo, setSegMotivo] = useState("");
  const [categoria, setCategoria] = useState("");
  const [personalAtendio, setPersonalAtendio] = useState("");
  const [estadoApoyo, setEstadoApoyo] = useState("");
  const [resultadoSel, setResultadoSel] = useState("");
  const [intentoSel, setIntentoSel] = useState("");
  // ATN-01 · M6.6 — al cambiar de caso, las SELECCIONES no textuales tampoco se arrastran: un
  // resultado elegido para un caso jamás debe quedar preseleccionado en otro.
  useEffect(() => {
    setResultadoSel(""); setIntentoSel(""); setSegMotivo("");
    setCategoria(""); setPersonalAtendio(""); setEstadoApoyo("");
  }, [casoId]);

  // 0) INSTITUCIONAL — ESCALAMIENTO INTERNO del centro. LAB7 · P1-A: quien ACUDIÓ se captura
  // aparte del actor que registra (nunca se autocompleta) y el estado del apoyo tiene valores
  // cerrados: haberlo pedido NO significa haberlo recibido.
  if (kind === "escalamiento_interno") {
    const cats = detail?.categorias_escalamiento || [];
    const estados = detail?.estados_apoyo || [];
    return (
      <div data-testid="v3-sa-control-escalamiento" className="mt-3 rounded-lg border border-amber-400/60 bg-white dark:bg-[#14243A] p-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300"><ClipboardList className="h-3.5 w-3.5" /> Escalamiento interno del centro</p>
        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">Indica a qué instancia interna se escaló. Quien acudió se registra aparte y sólo si lo escribes.</p>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="v3-sa-escalamiento-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)}
            className="rounded-md border border-slate-200 bg-white dark:bg-[#192C47] px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200">
            <option value="">Selecciona la instancia interna…</option>
            {cats.map((c) => <option key={c.codigo} value={c.codigo}>{c.etiqueta}</option>)}
          </select>
          <select data-testid="v3-sa-escalamiento-estado-apoyo" value={estadoApoyo} onChange={(e) => setEstadoApoyo(e.target.value)}
            className="rounded-md border border-slate-200 bg-white dark:bg-[#192C47] px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200">
            <option value="">Estado del apoyo (opcional)…</option>
            {estados.map((e) => <option key={e.codigo} value={e.codigo}>{e.etiqueta}</option>)}
          </select>
        </div>
        <input data-testid="v3-sa-escalamiento-personal" value={personalAtendio} maxLength={120}
          onChange={(e) => setPersonalAtendio(e.target.value)}
          placeholder="Personal del centro que acudió (opcional · nombre o turno)"
          className="mt-2 w-full rounded-lg border border-slate-200 bg-white dark:bg-[#192C47] px-2.5 py-1.5 text-sm text-slate-700 dark:text-slate-200" />
        <Textarea data-testid="v3-sa-escalamiento-nota" value={notaDe("esc_nota")} onChange={(e) => setNotaDe("esc_nota", e.target.value)} rows={2}
          placeholder="Nota opcional (hecho observable)." className="my-2 rounded-lg" />
        <Button data-testid="v3-sa-escalamiento-btn" disabled={busy || !categoria}
          onClick={async () => {
            const cid = casoId;
            const r = await (onRegistrarEscalamiento && onRegistrarEscalamiento({
              categoria_escalamiento: categoria, nota: notaDe("esc_nota").trim() || null,
              personal_atendio: personalAtendio.trim() || null, estado_apoyo: estadoApoyo || null,
            }));
            // Sólo se limpia con un resultado CONFIRMADO: un fallo absorbido no vacía el formulario.
            if (resuelto(r)) {
              limpiaOrigen(cid, ["esc_nota"], () => {
                setCategoria(""); setPersonalAtendio(""); setEstadoApoyo("");
              });
            }
          }}
          className="bg-amber-600 hover:bg-amber-700 text-white font-semibold">
          Registrar escalamiento interno
        </Button>
      </div>
    );
  }

  // 1) REGISTRAR QUÉ OCURRIÓ (resultado del audio con la persona).
  // v2.9.3 (continuidad operativa): también disponible DURANTE la fase de audio (kind 'audio' /
  // 'audio_en_curso'). Si el audio SIP no puede completarse (HM/Asterisk caído, la persona no
  // responde por audio, o la sesión queda colgada), el operador NO queda atascado: registra aquí
  // qué ocurrió y el backend avanza la ruta (desenlace/teléfono/contactos) hasta CERRAR. AudioTwoWay
  // sigue presente como vía primaria para hablar con la persona.
  // AUD-002: el CANAL no lo decide el cliente — lo deriva el backend del kind de la acción. Aquí
  // sólo se envía el hecho ejecutado (kind + resultado); si el caso no tuvo audio, el hecho no es
  // de audio (la proyección indica el kind con `sa.hecho_kind`).
  if (kind === "resultado" || kind === "audio" || kind === "audio_en_curso") {
    const enAudio = kind === "audio" || kind === "audio_en_curso";
    const hechoKind = sa.hecho_kind || "audio_persona";
    return (
      <div data-testid="v3-sa-control-resultado" className="mt-3 rounded-lg border border-[#01CBE3]/40 bg-white p-3">
        <p className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#0891A6] dark:text-[#01CBE3]"><ClipboardList className="h-3.5 w-3.5" /> Registrar qué ocurrió</p>
        {enAudio && <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">Tras hablar con la persona —o si el audio no fue posible— registra el resultado para continuar.</p>}
        {/* ATN-01 · M1 — resultado y nota describen la MISMA atención: se capturan en el mismo
            control y viajan en UN SOLO hecho. La nota sigue siendo opcional en esta rama. */}
        <Textarea data-testid="v3-sa-resultado-nota" value={notaDe("res_nota")} onChange={(e) => setNotaDe("res_nota", e.target.value)} rows={2}
          placeholder="Nota factual de lo ocurrido (opcional). Se registra junto con el resultado que elijas."
          className="mb-2 rounded-lg" />
        <div className="flex flex-wrap gap-1.5">
          {resultados.map((r) => (
            <button key={r.codigo} data-testid={`v3-sa-resultado-${r.codigo}`} disabled={busy}
              onClick={async () => {
                const cid = casoId;
                // La nota es OPCIONAL en esta rama: si está vacía no se envía el campo (el escritor
                // conserva su contrato; no se le manda un nulo que rechazaría).
                const _n = notaDe("res_nota").trim();
                const res = await onRegistrarAccion({ kind: hechoKind, resultado_codigo: r.codigo,
                                                      ...(_n ? { resultado_texto: _n } : {}) });
                if (resuelto(res)) limpiaOrigen(cid, ["res_nota"]);
              }}
              className={CHIP}>{r.etiqueta}</button>
          ))}
        </div>
      </div>
    );
  }

  // 2) REGISTRAR RESULTADO de una llamada (persona o contacto) — mismo journal Día 1.
  if (kind === "llamada_persona" || kind === "llamada_contacto") {
    const esContacto = kind === "llamada_contacto";
    const opts = esContacto ? resultados.filter((r) => CONTACTO_RESULTADOS.includes(r.codigo)) : resultados;
    const lista = opts.length ? opts : resultados;
    // ATN-01 · M4 — correlación EXPLÍCITA hacia adelante: el resultado se enlaza con el inicio ya
    // registrado SÓLO cuando ese inicio consta en la lectura autorizada del caso. Con varios
    // pendientes se pide elección explícita; nunca se elige «el último» en silencio.
    const pendientes = sa.intentos_pendientes || [];
    const unico = sa.intento_action_id || null;
    const faltaElegir = pendientes.length > 1 && !intentoSel;
    const refIntento = unico || (intentoSel || null);
    const ambiguo = sa.conteo_inequivoco === false;
    return (
      <div data-testid="v3-sa-control-llamada" className="mt-3 rounded-lg border border-[#01CBE3]/40 bg-white p-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#0891A6] dark:text-[#01CBE3]"><PhoneCall className="h-3.5 w-3.5" /> Registrar resultado de la llamada</p>
        {sa.telefono && <p className="mb-2 font-mono text-sm text-slate-500 dark:text-slate-300">{sa.telefono}</p>}
        {ambiguo && (
          <p data-testid="v3-sa-llamada-conteo-ambiguo" className="mb-2 rounded-md bg-amber-50 dark:bg-amber-900/20 px-2 py-1.5 text-xs text-amber-800 dark:text-amber-300">
            El número de intentos no es inequívoco. Hechos de llamada registrados en el caso: {sa.hechos_llamada ?? 0}.
          </p>
        )}
        {pendientes.length > 1 && (
          <div className="mb-2">
            <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">Hay varias llamadas iniciadas sin resultado. Indica a cuál corresponde este resultado.</p>
            <select data-testid="v3-sa-llamada-intento" value={intentoSel} onChange={(e) => setIntentoSel(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white dark:bg-[#192C47] px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200">
              <option value="">Selecciona la llamada iniciada…</option>
              {pendientes.map((p) => (
                <option key={p.action_id} value={p.action_id}>{fmtTimestamp(p.hora)} · {p.actor || "—"}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {lista.map((r) => (
            <button key={r.codigo} data-testid={`v3-sa-llamada-resultado-${r.codigo}`} disabled={busy || faltaElegir}
              onClick={async () => {
                const cid = casoId;
                await onRegistrarAccion({
                  kind: esContacto ? "llamada_contacto" : "llamada_persona",
                  objetivo: esContacto ? sa.contacto_id : null,
                  resultado_codigo: r.codigo,
                  ...(refIntento && !esContacto ? { metadata: { intento_action_id: refIntento } } : {}),
                });
                if (casoRef.current === cid) setIntentoSel("");
              }}
              className={CHIP}>{r.etiqueta}</button>
          ))}
        </div>
      </div>
    );
  }

  // 2b) v2.13.1 — RECORDATORIO PROGRAMADO: registrar el WhatsApp MANUAL que la monitorista envió
  // por su cuenta tras los intentos de llamada. Galarya NO envía nada: sólo registra el hecho.
  if (kind === "whatsapp_manual") {
    return (
      <div data-testid="v3-sa-control-whatsapp" className="mt-3 rounded-lg border border-emerald-500/40 bg-white p-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300"><MessageSquare className="h-3.5 w-3.5" /> WhatsApp manual</p>
        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
          Envía el WhatsApp desde tu equipo y regístralo aquí. Galarya no envía mensajes automáticos.
        </p>
        <Textarea data-testid="v3-sa-whatsapp-nota" value={notaDe("wa_nota")} onChange={(e) => setNotaDe("wa_nota", e.target.value)} rows={2}
          placeholder="Nota opcional (a quién se envió, qué se escribió)." className="mb-2 rounded-lg" />
        <Button data-testid="v3-sa-whatsapp-btn" disabled={busy}
          onClick={async () => {
            const cid = casoId;
            const _w = notaDe("wa_nota").trim();
            const r = await onRegistrarAccion({ kind: "whatsapp_manual", ...(_w ? { resultado_texto: _w } : {}) });
            if (resuelto(r)) limpiaOrigen(cid, ["wa_nota"]);
          }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
          Registrar WhatsApp manual enviado
        </Button>
      </div>
    );
  }

  // 3) REGISTRAR DESENLACE: lo cubre de forma prominente y canal-agnóstica DesenlaceDirectoControl
  // (v2.9.5), visible siempre. Aquí NO se duplica el control.

  // H3 (v2.9.6) — SEGUIMIENTO POST-EMERGENCIA con TERMINAL. El tercero (ambulancia/hospital/familiar)
  // puede no responder; el caso NO se inmortaliza: se cumple o se marca "no obtenible" con motivo.
  if (kind === "seguimiento_emergencia") {
    return (
      <div data-testid="v3-sa-control-seguimiento" className="mt-3 rounded-lg border border-[#9B2C2C]/40 bg-white p-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#9B2C2C] dark:text-[#F2B8B8]"><ClipboardList className="h-3.5 w-3.5" /> Seguimiento post-emergencia</p>
        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">Confirma el seguimiento del servicio de emergencia. Si el tercero no responde y no es obtenible, regístralo con un motivo — el caso podrá cerrarse sin inventar el resultado.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="v3-sa-seguimiento-cumplido" disabled={busy}
            onClick={() => onResolverSeguimiento && onResolverSeguimiento({ resultado: "cumplido" })}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
            Seguimiento cumplido
          </Button>
          <select data-testid="v3-sa-seguimiento-motivo" value={segMotivo} onChange={(e) => setSegMotivo(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200">
            <option value="">No obtenible — motivo…</option>
            <option value="tercero_no_responde">El tercero no responde</option>
            <option value="hospital_no_informa">El hospital no informa</option>
            <option value="familiar_no_localizable">Familiar no localizable</option>
            <option value="sin_folio_disponible">Sin folio disponible</option>
            <option value="otro">Otro motivo</option>
          </select>
          <Button data-testid="v3-sa-seguimiento-no-obtenible" disabled={busy || !segMotivo}
            onClick={() => { onResolverSeguimiento && onResolverSeguimiento({ resultado: "no_obtenible", motivo: segMotivo }); setSegMotivo(""); }}
            className="bg-[#9B2C2C] hover:bg-[#822424] text-white font-semibold">
            Marcar no obtenible
          </Button>
        </div>
      </div>
    );
  }

  // 4) ACCIÓN FINAL documentada (protocolo base / ruta agotada / atribución ambigua / por ubicación).
  if (permitidas.includes("accion_final")) {
    const porUbicacion = sa.codigo === "atender_ubicacion" || sa.codigo === "verificar_ubicacion";
    const inst = sa.codigo === "verificar_ubicacion";
    return (
      <div data-testid="v3-sa-control-accionfinal" className="gv2-plain mt-3 p-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide gv2-amber-text"><ClipboardList className="h-3.5 w-3.5" /> {inst ? "Registrar aviso y verificación en la ubicación" : porUbicacion ? "Registrar gestión por ubicación" : "Registrar gestión final"}</p>
        <Textarea data-testid="v3-sa-accionfinal-nota" value={notaDe("af_nota")} onChange={(e) => setNotaDe("af_nota", e.target.value)} rows={2}
          placeholder={inst ? "Describe el aviso al personal del centro y la verificación en la ubicación (obligatorio)." : porUbicacion ? "Describe la verificación/gestión realizada en el sitio (obligatorio). No inventes identidad." : "Describe la gestión final realizada (obligatorio)."} className="mb-2 rounded-lg" />
        {/* ATN-01 · M1-A — ATENCIÓN CONCLUIDA: la nota y el resultado observable describen la MISMA
            atención, así que viajan en UNA SOLA acción. Si el resultado todavía no consta, la
            captura parcial (sólo nota) se conserva y el servidor pedirá después lo que falte. */}
        {inst && resultados.length > 0 && (
          <div className="mb-2">
            <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">¿Ya hay un resultado observable de la verificación? Puedes registrarlo aquí mismo.</p>
            <select data-testid="v3-sa-accionfinal-resultado" value={resultadoSel} onChange={(e) => setResultadoSel(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white dark:bg-[#192C47] px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200">
              <option value="">Resultado pendiente — lo registraré después</option>
              {resultados.map((r) => <option key={r.codigo} value={r.codigo}>{r.etiqueta}</option>)}
            </select>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="v3-sa-accionfinal-btn" disabled={busy || !notaDe("af_nota").trim()}
            onClick={async () => {
              const cid = casoId;
              const r = await onRegistrarAccion({
                kind: "accion_final", resultado_texto: notaDe("af_nota").trim(),
                ...(inst && resultadoSel ? { resultado_codigo: resultadoSel } : {}),
                metadata: porUbicacion ? { via: "ubicacion", ubicacion: sa.ubicacion } : { protocolo_base_por_plan_incompleto: true },
              });
              if (resuelto(r)) limpiaOrigen(cid, ["af_nota"], () => setResultadoSel(""));
            }}
            /* GC105 · acción primaria con azul de TEXTO (contraste ≥4,5:1 en día y noche): el
               barrido nocturno dejaba este botón con la MISMA superficie elevada que un control
               deshabilitado. Al llevar clases gv2-* queda fuera del barrido y el estado
               deshabilitado sigue mandando por especificidad. */
            className="gv2-primary gv2-primary-text bg-[#0068D6] hover:bg-[#0055B0] text-white font-semibold">
            {inst ? (resultadoSel ? "Registrar verificación y resultado" : "Registrar verificación del personal")
              : porUbicacion ? "Registrar gestión por ubicación" : "Registrar gestión final"}
          </Button>
          {/* ATN-01 · M1-B — SÓLO AVISO o avance ANTERIOR a la verificación: queda como nota de la
              gestión. No registra acción final ni un progreso presencial que no ocurrió. */}
          {inst && onNotaGestion && (
            <Button data-testid="v3-sa-avance-sin-verificacion" variant="outline" disabled={busy || !notaDe("af_nota").trim()}
              onClick={async () => {
                const cid = casoId;
                const r = await onNotaGestion(notaDe("af_nota").trim());
                if (resuelto(r)) limpiaOrigen(cid, ["af_nota"]);
              }}
              className="border-slate-300 text-slate-600 dark:text-slate-300 font-semibold">
              Registrar avance sin verificación
            </Button>
          )}
        </div>
        {inst && (
          <p className="mt-2 text-[11px] text-slate-400">
            Quien acudió se registra aparte y sólo si lo escribes: registrar no es haber atendido.
          </p>
        )}
      </div>
    );
  }

  return null;
}
